<template>
  <div class="public-photos" v-loading="loading">
    <div v-if="!readonly" class="tip">
      点击「+」上传公开照片，单张不超过 5MB，支持 jpg/png/jpeg
    </div>
    <div class="photo-grid">
      <div v-for="url in images" :key="url" class="photo-item">
        <el-image
          class="photo-image"
          :src="buildImageUrl(url, 800)"
          fit="cover"
          :preview-src-list="previewList"
        />
        <el-button
          v-if="!readonly"
          class="photo-delete"
          type="text"
          icon="el-icon-delete"
          @click="handleDelete(url)"
        >删除</el-button>
      </div>
      <el-upload
        v-if="!readonly"
        class="photo-uploader"
        action=""
        multiple
        accept="image/jpeg,image/png,image/jpg"
        :show-file-list="false"
        :disabled="uploading"
        :http-request="customUploadRequest"
        :before-upload="beforeFileUpload"
      >
        <div class="photo-add">
          <i :class="uploading ? 'el-icon-loading' : 'el-icon-plus'"></i>
          <span>{{ uploading ? "上传中" : "上传" }}</span>
        </div>
      </el-upload>
    </div>
    <div v-if="!loading && images.length === 0" class="empty-text">暂无公开照片</div>
  </div>
</template>

<script>
import { ossClient } from "@/plugins/upload";

export default {
  name: "PublicPhotos",
  props: {
    teacherId: {
      type: String,
      default: "",
    },
    // 查看态只读:不显示上传入口与删除按钮
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      uploading: false,
      images: [],
      // 本轮选择的多张图全部传完后一次性提交,避免并发 add 的响应互相覆盖
      pendingUrls: [],
      pendingCount: 0,
    };
  },
  computed: {
    // 与缩略图同宽:el-image 用 previewSrcList.indexOf(src) 定位初始下标,
    // 两处 URL 必须一致,否则点任意一张都会从第一张开始预览
    previewList() {
      return this.images.map((url) => this.buildImageUrl(url, 800));
    },
  },
  watch: {
    teacherId() {
      this.fetchList();
    },
  },
  created() {
    this.fetchList();
  },
  methods: {
    buildImageUrl(url, width) {
      if (!url) {
        return "";
      }
      const normalized = String(url).replace(/^http:\/\//, "https://");
      const separator = normalized.includes("?") ? "&" : "?";
      return `${normalized}${separator}x-oss-process=image/resize,w_${width}/quality,q_80`;
    },
    async fetchList() {
      if (!this.teacherId) {
        this.images = [];
        return;
      }
      this.loading = true;
      try {
        const { data } = await this.$http.get(
          `teachers/public_photo/${this.teacherId}`
        );
        this.images = (data && data.publicPhotos) || [];
      } catch (error) {
        this.$message.error("公开照片加载失败");
      } finally {
        this.loading = false;
      }
    },
    beforeFileUpload(file) {
      const isImage = ["image/jpeg", "image/png", "image/jpg"].includes(file.type);
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isImage) {
        this.$message.error("只能上传 jpg/png/jpeg 文件");
        return false;
      }
      if (!isLt5M) {
        this.$message.error("上传图片大小不能超过 5MB");
        return false;
      }
      // 与服务端上限保持一致,避免传完才被拒、留下 OSS 孤儿文件
      if (this.images.length + this.pendingUrls.length + this.pendingCount >= 50) {
        this.$message.error("公开照片最多上传50张");
        return false;
      }
      return true;
    },
    async customUploadRequest(config) {
      const { file } = config;
      if (!this.teacherId) {
        this.$message.error("缺少讲师ID，无法上传");
        config.onError();
        return;
      }

      this.pendingCount += 1;
      this.uploading = true;
      try {
        if (!ossClient.client) {
          await ossClient.getCredential();
        }

        const timestamp = new Date().getTime();
        const path = `teachers/${this.teacherId}/public-photos/${timestamp}-${file.name}`;
        const result = await ossClient.client.put(path, file, {
          progress: (p) => {
            if (config.onProgress) {
              config.onProgress({ percent: p * 100 });
            }
          },
        });

        if (result && result.res && result.res.status === 200) {
          const url = String(result.url).replace(/^http:\/\//, "https://");
          this.pendingUrls.push(url);
          config.onSuccess({ url, name: file.name });
        } else {
          throw new Error("Upload failed");
        }
      } catch (error) {
        console.error("OSS Upload Error:", error);
        this.$message.error("上传失败，请重试");
        config.onError(error);
      } finally {
        this.pendingCount = Math.max(this.pendingCount - 1, 0);
        if (this.pendingCount === 0) {
          this.uploading = false;
          await this.submitUploaded();
        }
      }
    },
    async submitUploaded() {
      const urls = this.pendingUrls.splice(0);
      if (!urls.length) {
        return;
      }
      try {
        const { data } = await this.$http.post("teachers/public_photo/add", {
          teacherId: this.teacherId,
          photos: urls,
        });
        this.images = (data && data.publicPhotos) || this.images;
      } catch (error) {
        this.$message.error("保存上传的图片失败，请重试");
        this.fetchList();
      }
    },
    async handleDelete(url) {
      try {
        await this.$confirm("确定删除这张图片吗？", "提示", {
          type: "warning",
        });
      } catch (error) {
        return;
      }
      try {
        const { data } = await this.$http.post("teachers/public_photo/remove", {
          teacherId: this.teacherId,
          url,
        });
        this.images = (data && data.publicPhotos) || [];
        this.$message.success("删除成功");
      } catch (error) {
        this.$message.error("删除失败，请重试");
      }
    },
  },
};
</script>

<style scoped>
.public-photos {
  min-height: 400px;
  padding: 10px;
}
.tip {
  margin-bottom: 10px;
  color: #909399;
  font-size: 12px;
}
.photo-grid {
  display: flex;
  flex-wrap: wrap;
}
.photo-item,
.photo-uploader {
  width: 120px;
  margin: 0 12px 12px 0;
}
.photo-image {
  display: block;
  width: 120px;
  height: 120px;
  border-radius: 4px;
}
.photo-delete {
  width: 100%;
  color: #f56c6c;
}
.photo-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 120px;
  height: 120px;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  color: #8c939d;
  cursor: pointer;
}
.photo-add:hover {
  border-color: #409eff;
  color: #409eff;
}
.photo-add i {
  margin-bottom: 6px;
  font-size: 24px;
}
.empty-text {
  color: #909399;
  font-size: 13px;
}
</style>
